import axios from 'axios';
import { basicConfig } from '../config';

export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
}

// Ensure authentication token is set before sending logs
let tokenInitialized = false;

const ensureTokenReady = async () => {
  if (!tokenInitialized && window) {
    const queryParameters = new URLSearchParams(window.location.search);
    let xSignature = queryParameters.get('x-signature');

    // Fallback to localStorage if not in URL
    if (!xSignature) {
      xSignature = window.localStorage.getItem('xSignature');
    }

    if (xSignature) {
      axios.defaults.headers.common.Authorization = `Signature ${xSignature}`;
      tokenInitialized = true;
    }
  }
};

interface LogContext {
  userId?: string;
  assessmentId?: string;
  testId?: string;
  url?: string;
  status?: number;
  error?: string;
  stack?: string;
  componentStack?: string;
  [key: string]: any;
}

class Logger {
  private queue: Array<{ level: LogLevel; message: string; context: LogContext }> = [];

  private isProcessing = false;

  private readonly maxQueueSize = 50;

  private readonly batchDelay = 2000; // 2 seconds

  async log(level: LogLevel, message: string, context: LogContext = {}) {
    // Enrich context with current page URL
    const enrichedContext = {
      ...context,
      url: context.url || window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    // Always log to browser console for debugging
    /* eslint-disable no-console */
    if (level === LogLevel.ERROR) {
      console.error(`[GridFit ${level.toUpperCase()}]`, message, enrichedContext);
    } else if (level === LogLevel.WARN) {
      console.warn(`[GridFit ${level.toUpperCase()}]`, message, enrichedContext);
    } else {
      console.info(`[GridFit ${level.toUpperCase()}]`, message, enrichedContext);
    }
    /* eslint-enable no-console */

    // Add to queue for sending to backend
    this.queue.push({ level, message, context: enrichedContext });

    // Limit queue size
    if (this.queue.length > this.maxQueueSize) {
      this.queue.shift();
    }

    this.processQueue();
  }

  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    // Wait a bit to batch multiple logs
    await new Promise(resolve => setTimeout(resolve, this.batchDelay));

    const logsToSend = [...this.queue];
    this.queue = [];

    // Ensure authentication token is ready before sending
    await ensureTokenReady();

    // Don't send logs to backend if no authentication token is available
    // Logs will still appear in browser console for debugging
    const hasToken = axios.defaults.headers.common.Authorization;

    if (!hasToken) {
      this.isProcessing = false;
      return;
    }

    try {
      // Send each log to backend directly using axios to avoid circular dependency
      await Promise.allSettled(
        logsToSend.map((
          { level, message, context },
        ) => axios.post(`${basicConfig.gridFitApi}/api/logging`, {
          level,
          message,
          context,
        })),
      );
    } finally {
      this.isProcessing = false;

      // Process remaining queue if needed
      if (this.queue.length > 0) {
        this.processQueue();
      }
    }
  }

  error(message: string, context?: LogContext) {
    return this.log(LogLevel.ERROR, message, context);
  }

  warn(message: string, context?: LogContext) {
    return this.log(LogLevel.WARN, message, context);
  }

  info(message: string, context?: LogContext) {
    return this.log(LogLevel.INFO, message, context);
  }
}

export const LoggerService = new Logger();
